/**
 * http.service.ts
 *
 * Author: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */

import bluebird from "bluebird";

import { errorFor, HttpError } from "../models/error";

export interface HttpService {
  get<T, S extends Partial<Record<keyof S, string | undefined>> = Record<string, never>>(path: string, params?: S): Promise<T>,
  head<S extends Record<string, string>>(path: string, params?: S): Promise<Headers>,
  post<T>(path: string, data: T, token?: string): Promise<T>,
  post<T, S>(path: string, data: T, token?: string): Promise<S>,
  put<T>(path: string, data: T): Promise<T>,
  put<T, S>(path: string, data: T): Promise<S>,
  delete<T>(path: string): Promise<T>,
  delete<S = void>(path: string): Promise<S>,

  setDomain(domain: string): void,
  currentDomain(): string,
  setTokenFactory(factory: (path: string) => Promise<string | null>): void,

  getText<S extends Partial<Record<keyof S, string | undefined>>>(path: string, params?: S): Promise<string>,

  websocket(deviceID: string): Promise<WebSocket>
}


class HTTPService implements HttpService {
  tokenFactory: (path: string) => Promise<string | null>;

  private domain: string;

  constructor() {
    this.domain = "";
    this.tokenFactory = (): Promise<string> => bluebird.resolve("");
  }

  setDomain(domain: string) {
    this.domain = domain;
  }

  currentDomain(): string {
    return this.domain;
  }

  async get<T, S extends Partial<Record<keyof S, string | undefined>> = Record<string, never>>(path: string, params?: S): Promise<T> {

    if (params) {
      const queryParams = new URLSearchParams();
      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          const p = params[key];
          p && queryParams.append(key, p);
        }
      }
      path = path + "?" + queryParams.toString();
    }

    const headers: any = new Headers({ "X-Requested-With": "XMLHttpRequest" });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }

    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    } const response = await fetch(path, {
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Couldn't fetch:", data, response);
      throw errorFor(data, response.status);
    }
    //console.log("data:", data);
    return data;
  }

  async head<S extends Partial<Record<keyof S, string | undefined>> = Record<string, never>>(path: string, params?: S): Promise<Headers> {

    if (params) {
      const queryParams = new URLSearchParams();
      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          const p = params[key];
          p && queryParams.append(key, p);
        }
      }
      path = path + "?" + queryParams.toString();
    }

    const headers: any = new Headers({ "X-Requested-With": "XMLHttpRequest" });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }

    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    } const response = await fetch(path, {
      method: "HEAD",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers
    });

    if (!response.ok) {
      const message = response.headers.get("x-error");
      console.error("Couldn't fetch:", message, response);
      throw new HttpError(message || "Bad Server Response", response.status);
    }
    //console.log("data:", data);
    return response.headers;
  }

  async getText<S extends Partial<Record<keyof S, string | undefined>> = Record<string, never>>(path: string, params?: S): Promise<string> {

    if (params) {
      const queryParams = new URLSearchParams();
      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          const p = params[key];
          p && queryParams.append(key, p);
        }
      }
      path = path + "?" + queryParams.toString();
    }

    const headers: any = new Headers({ "X-Requested-With": "XMLHttpRequest" });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }

    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    } const response = await fetch(path, {
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers
    });

    if (response.ok) {
      const data = await response.text();
      return data;
    } else {
      const data = await response.json();
      console.error("Couldn't fetch:", data, response);
      throw errorFor(data, response.status);
    }
  }

  async post<T, S>(path: string, data: T, token: string | null = null): Promise<S> {

    const headers = new Headers({
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    });
    if (!token) { token = await this.tokenFactory(path); }
    if (token) { headers.append("Authorization", token); }
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    }

    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers,
      body: JSON.stringify(data)
    });
    const body = await response.json();

    if (!response.ok) {
      console.error("Couldn't post data:", body, response);
      throw errorFor(body, response.status, "Failed Posting to Server");
    }

    //console.log("data:", body);
    return body;
  }

  async put<T, S>(path: string, data: T): Promise<S> {

    const headers = new Headers({
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    } const response = await fetch(path, {
      method: "PUT",
      credentials: "same-origin",
      mode: "same-origin",
      headers: headers,
      body: JSON.stringify(data)
    });
    const body = await response.json();

    if (!response.ok) {
      console.error("Couldn't post data:", body, response);
      throw errorFor(body, response.status, "Failed Posting to Server");
    }

    //console.log("data:", body);
    return body;
  }

  async delete<T = void, S extends Partial<Record<keyof S, string | undefined>> = Record<string, never>>(path: string, params?: S): Promise<T> {

    if (params) {
      const queryParams = new URLSearchParams();
      for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          const p = params[key];
          p && queryParams.append(key, p);
        }
      }
      path = path + "?" + queryParams.toString();
    }

    const headers = new Headers({ "X-Requested-With": "XMLHttpRequest" });
    const token = await this.tokenFactory(path);
    if (token) { headers.append("Authorization", token); }
    if (this.domain) {
      path = this.domain.startsWith("http") ? `${this.domain}${path}` : `//${this.domain}${path}`;
    } const response = await fetch(path, {
      method: "DELETE",
      credentials: "same-origin",
      headers: headers,
      mode: "same-origin"
    });
    const body = await response.json();

    if (!response.ok) {
      console.error("Couldn't delete data", body, response);
      throw errorFor(body, response.status, "Failed Deleting from Server");
    }

    //console.log("data:", body);
    return body;
  }

  async websocket(deviceID: string): Promise<WebSocket> {
    const token = await this.tokenFactory("");
    let url: string;

    if (!token) {
      throw new Error("Not Authenticated");
    }
    if (this.domain) {
      const adr = this.domain.replace("https://", "");
      url = `wss://${adr}/socket/${deviceID}/`;
    } else {
      url = `wss://${window.location.host}/socket/${deviceID}/`;
    }
    return new WebSocket(url, token);
  }

  setTokenFactory(factory: (path: string) => Promise<string | null>): void {
    this.tokenFactory = factory;
  }
}

export function httpTestFactory(): HttpService {
  return new HTTPService();
}

export const http = new HTTPService();
